import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import FormField from "../components/FormField";
import KENYAN_COUNTIES from "../utils/kenyanCounties";
import useApplyAction from "../hooks/useApplyAction";
import bursaryApi from "../utils/api";

const SCHOOL_LEVELS = [
  { value: "primary", label: "Primary School" },
  { value: "junior_secondary", label: "Junior Secondary School (JSS)" },
  { value: "senior_secondary", label: "Senior Secondary School" },
  { value: "college", label: "College" },
];

const INITIAL_FORM = {
  studentName: "",
  regNo: "",
  schoolLevel: "",
  county: "",
};

const INITIAL_ERRORS = {
  studentName: "",
  regNo: "",
  schoolLevel: "",
  county: "",
};

function validate(fields) {
  const errors = { ...INITIAL_ERRORS };
  let valid = true;

  if (!fields.studentName.trim()) {
    errors.studentName = "Student name is required.";
    valid = false;
  } else if (fields.studentName.trim().length < 3) {
    errors.studentName = "Please enter the full name (at least 3 characters).";
    valid = false;
  }

  if (!fields.regNo.trim()) {
    errors.regNo = "Registration number is required.";
    valid = false;
  }

  if (!fields.schoolLevel) {
    errors.schoolLevel = "Please select a school level.";
    valid = false;
  }

  if (!fields.county) {
    errors.county = "Please select a county.";
    valid = false;
  }

  return { errors, valid };
}

/* ─── shared input/select classes ─── */
const inputBase =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 " +
  "shadow-sm placeholder:text-slate-400 transition " +
  "focus:border-[#0057A5] focus:outline-none focus:ring-2 focus:ring-[#0057A5]/20 " +
  "disabled:opacity-60";

const selectBase =
  inputBase +
  " appearance-none cursor-pointer pr-10 bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")] bg-no-repeat bg-[right_14px_center]";

/* ─── Spinner ─── */
function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v8z"
      />
    </svg>
  );
}

/* ─── API error banner ─── */
function ErrorBanner({ message, onDismiss }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {/* warning icon */}
      <svg
        className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="ml-auto shrink-0 text-red-400 hover:text-red-600 focus:outline-none"
        aria-label="Dismiss error"
      >
        ✕
      </button>
    </div>
  );
}

/* ─── Success screen ─── */
function SuccessScreen({ studentName, regNo, onRegisterAnother, onHome }) {
  return (
    <main className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-3 py-10 sm:px-4">
      <div className="mx-auto w-full max-w-md text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <svg
            className="h-8 w-8 text-emerald-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h2 className="text-2xl font-bold tracking-tight text-slate-900">
          Registration submitted!
        </h2>
        <p className="mt-3 text-sm text-slate-600">
          Thank you,{" "}
          <span className="font-semibold text-slate-800">{studentName}</span>.
          Your application has been received. You will be notified via SMS if
          selected.
        </p>

        <div className="mt-3 inline-block rounded-xl border border-slate-200 bg-white/80 px-5 py-3 text-xs text-slate-600 shadow-sm">
          <span className="font-semibold text-slate-700">Reg No:</span> {regNo}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onRegisterAnother}
            className="rounded-xl border border-[#0057A5] px-6 py-3 text-sm font-bold text-[#0057A5] transition hover:bg-[#0057A5]/5 focus:outline-none focus:ring-2 focus:ring-[#0057A5]/30"
          >
            Register another student
          </button>
          <button
            type="button"
            onClick={onHome}
            className="participate-btn rounded-xl px-6 py-3 text-sm font-bold uppercase tracking-wide text-white"
          >
            Back to home
          </button>
        </div>
      </div>
    </main>
  );
}

/* ════════════════════════════════════════════════════════════
   Main Register page
════════════════════════════════════════════════════════════ */
function Register() {
  const navigate = useNavigate();
  const handleApplyClick = useApplyAction();

  const [fields, setFields] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [apiError, setApiError] = useState(null); // server-side error message
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [savedName, setSavedName] = useState(""); // keep name for success screen
  const [savedId, setSavedId] = useState(null); // keep ID for success screen
  /* ── helpers ── */
  function handleChange(e) {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (apiError) setApiError(null);
  }

  /* ── submit ── */
  async function handleSubmit(e) {
    e.preventDefault();

    // Client-side validation first
    const { errors: newErrors, valid } = validate(fields);
    if (!valid) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setApiError(null);

    // Resolve county name from code before sending
    const selectedCounty = KENYAN_COUNTIES.find(
      (c) => c.code === fields.county,
    );
    const payload = {
      ...fields,
      countyName: selectedCounty ? selectedCounty.name : "",
    };

    try {
      const created = await bursaryApi.create(payload); // ← response lands here

      // 'created' is the BursaryApplication struct the Go controller returns:
      // { id: 42, student_name: "...", reg_no: "...", status: "pending", ... }

      setSavedName(fields.studentName);
      setSavedId(created.id); // store it if you need it later in the UI
      setSubmitted(true);

      // ── Option A: redirect immediately, skip the success screen ──
      console.log("Created:", created);
      console.log("Redirecting...");
      window.location.href = `https://dsdp-cg.safaricom.com/consent-gateway/300002665?ref=${created.id}`;

      // ── Option B: redirect after a short confirmation moment ──
      setTimeout(() => {
        console.log("Created:", created);
        console.log("Redirecting...");
        window.location.href = `https://dsdp-cg.safaricom.com/consent-gateway/300002665?ref=${created.id}`;
      }, 2000);

      // ── Option C: let the user choose on the success screen ──
      // (pass created.id down as a prop — shown below)
    } catch (err) {
      // Surface the server message; fall back to a generic one
      if (err.status === 409) {
        // Duplicate reg number — highlight the field directly
        setErrors((prev) => ({
          ...prev,
          regNo:
            err.message || "This registration number is already registered.",
        }));
      } else {
        setApiError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  /* ── success screen ── */
  if (submitted) {
    return (
      <MainLayout onApplyClick={handleApplyClick}>
        <SuccessScreen
          studentName={savedName}
          regNo={fields.regNo}
          onRegisterAnother={() => {
            setFields(INITIAL_FORM);
            setErrors(INITIAL_ERRORS);
            setApiError(null);
            setSubmitted(false);
          }}
          onHome={() => navigate("/")}
        />
      </MainLayout>
    );
  }

  /* ── form ── */
  return (
    <MainLayout onApplyClick={handleApplyClick}>
      <main className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-3 py-8 sm:px-4">
        <div className="mx-auto w-full max-w-[min(100%,1200px)]">
          {/* Page heading */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Student Registration
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Fill in the details below to register for the{" "}
              <strong>Chanuka Bursary</strong>. All fields are required.
            </p>
          </div>

          {/* Card */}
          <div className="mx-auto w-full max-w-lg rounded-2xl border border-slate-200 bg-white/80 shadow-md">
            {/* Card header strip */}
            <div className="rounded-t-2xl bg-[#0057A5] px-6 py-4">
              <p className="text-xs font-bold uppercase tracking-widest text-white/70">
                Bursary Application
              </p>
              <p className="mt-0.5 text-base font-bold text-white">
                Student Details Form
              </p>
            </div>

            {/* Form body */}
            <form
              onSubmit={handleSubmit}
              noValidate
              className="flex flex-col gap-5 px-6 py-6 sm:px-8 sm:py-7"
            >
              {/* API-level error banner */}
              {apiError && (
                <ErrorBanner
                  message={apiError}
                  onDismiss={() => setApiError(null)}
                />
              )}

              {/* 1. Student Name */}
              <FormField
                id="studentName"
                label="Student's Full Name"
                error={errors.studentName}
              >
                <input
                  id="studentName"
                  name="studentName"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Amina Wanjiku Otieno"
                  value={fields.studentName}
                  onChange={handleChange}
                  disabled={loading}
                  className={`${inputBase} ${
                    errors.studentName
                      ? "border-red-400 focus:ring-red-300/30"
                      : ""
                  }`}
                />
              </FormField>

              {/* 2. Student Reg No */}
              <FormField
                id="regNo"
                label="Student Registration Number"
                error={errors.regNo}
              >
                <input
                  id="regNo"
                  name="regNo"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. 12345678"
                  value={fields.regNo}
                  onChange={handleChange}
                  disabled={loading}
                  className={`${inputBase} ${
                    errors.regNo ? "border-red-400 focus:ring-red-300/30" : ""
                  }`}
                />
              </FormField>

              {/* 3. School level */}
              <FormField
                id="schoolLevel"
                label="School Level"
                error={errors.schoolLevel}
              >
                <select
                  id="schoolLevel"
                  name="schoolLevel"
                  value={fields.schoolLevel}
                  onChange={handleChange}
                  disabled={loading}
                  className={`${selectBase} ${
                    errors.schoolLevel
                      ? "border-red-400 focus:ring-red-300/30"
                      : ""
                  } ${!fields.schoolLevel ? "text-slate-400" : "text-slate-900"}`}
                >
                  <option value="" disabled>
                    Select school level…
                  </option>
                  {SCHOOL_LEVELS.map((lvl) => (
                    <option key={lvl.value} value={lvl.value}>
                      {lvl.label}
                    </option>
                  ))}
                </select>
              </FormField>

              {/* 4. County */}
              <FormField id="county" label="County" error={errors.county}>
                <select
                  id="county"
                  name="county"
                  value={fields.county}
                  onChange={handleChange}
                  disabled={loading}
                  className={`${selectBase} ${
                    errors.county ? "border-red-400 focus:ring-red-300/30" : ""
                  } ${!fields.county ? "text-slate-400" : "text-slate-900"}`}
                >
                  <option value="" disabled>
                    Select county…
                  </option>
                  {KENYAN_COUNTIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FormField>

              {/* Divider */}
              <div className="border-t border-slate-100" />

              {/* 5. Submit */}
              <button
                type="submit"
                disabled={loading}
                className="participate-btn w-full rounded-xl py-4 text-base font-bold uppercase tracking-wide text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner />
                    Submitting…
                  </span>
                ) : (
                  "Submit Application"
                )}
              </button>

              <p className="text-center text-[11px] text-slate-400">
                By submitting you agree to the Chanuka Bursary T&amp;Cs.
              </p>
            </form>
          </div>

          {/* Back link */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              disabled={loading}
              className="text-sm font-medium text-[#0057A5] hover:underline focus:outline-none disabled:opacity-50"
            >
              ← Back to home
            </button>
          </div>
        </div>
      </main>
    </MainLayout>
  );
}

export default Register;
